import { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  ArrowLeft,
  Trophy,
  TrendingUp,
  TrendingDown,
  ArrowUpDown,
  BarChart3,
  Sparkles,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import { API } from '../lib/api';
import { cn } from '../lib/utils';

const sortOptions = [
  { key: 'points', label: 'Points' },
  { key: 'nrr', label: 'Run Rate' },
  { key: 'won', label: 'Wins' },
  { key: 'played', label: 'Played' },
  { key: 'team_name', label: 'Team' },
];

const SortChip = ({ active, children, onClick }) => (
  <button
    onClick={onClick}
    className={cn(
      'rounded-full border px-3 py-2 text-sm font-medium transition-all',
      active
        ? 'border-primary/30 bg-primary/12 text-primary'
        : 'border-white/10 bg-white/[0.04] text-white/58 hover:bg-white/[0.08] hover:text-white'
    )}
  >
    {children}
  </button>
);

const nrrTone = (value) => (value >= 0 ? 'text-emerald-300' : 'text-red-300');

const getSortValue = (team, key) => {
  if (key === 'team_name') return team.team_name?.toLowerCase() || '';
  return team[key];
};

const sortTable = (rows, sortKey, direction) => {
  const sorted = [...rows].sort((a, b) => {
    const aValue = getSortValue(a, sortKey);
    const bValue = getSortValue(b, sortKey);

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return aValue.localeCompare(bValue);
    }

    return (aValue ?? 0) - (bValue ?? 0);
  });

  sorted.reverse();

  if (sortKey === 'team_name') {
    return direction === 'asc' ? [...sorted].reverse() : sorted;
  }

  return direction === 'desc' ? sorted : [...sorted].reverse();
};

const PointsTableLoading = () => (
  <Layout title="Standings">
    <div className="space-y-6">
      <div className="h-36 animate-pulse rounded-[28px] border border-white/10 bg-white/[0.04]" />
      <div className="h-16 animate-pulse rounded-2xl border border-white/10 bg-white/[0.04]" />
      <div className="h-[420px] animate-pulse rounded-[28px] border border-white/10 bg-white/[0.04]" />
    </div>
  </Layout>
);

const PointsTable = () => {
  const { tournamentId } = useParams();
  const { getAuthHeader } = useAuth();
  const [tournament, setTournament] = useState(null);
  const [pointsTable, setPointsTable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState('points');
  const [sortDirection, setSortDirection] = useState('desc');

  const fetchData = useCallback(async (showError = true) => {
    try {
      const [tournamentRes, pointsRes] = await Promise.all([
        axios.get(`${API}/tournaments/${tournamentId}`, getAuthHeader()),
        axios.get(`${API}/tournaments/${tournamentId}/points-table`, getAuthHeader()),
      ]);
      setTournament(tournamentRes.data);
      setPointsTable(pointsRes.data);
    } catch (error) {
      if (showError) toast.error('Failed to load points table');
    } finally {
      setLoading(false);
    }
  }, [tournamentId, getAuthHeader]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useAutoRefresh(() => fetchData(false));

  const sortedTable = useMemo(() => sortTable(pointsTable, sortKey, sortDirection), [pointsTable, sortKey, sortDirection]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDirection((prev) => (prev === 'desc' ? 'asc' : 'desc'));
      return;
    }

    setSortKey(key);
    setSortDirection(key === 'team_name' ? 'asc' : 'desc');
  };

  if (loading) {
    return <PointsTableLoading />;
  }

  return (
    <Layout title="Standings">
      <div className="space-y-6">
        <Card className="overflow-hidden rounded-[28px] border-primary/20 bg-[linear-gradient(130deg,rgba(170,255,0,0.14),rgba(255,255,255,0.03)_55%,rgba(255,255,255,0.01))]">
          <CardContent className="p-6 sm:p-8">
            <div className="grid gap-6 lg:grid-cols-[1.15fr_0.85fr]">
              <div>
                <Link to={`/tournaments/${tournamentId}`}>
                  <Button variant="ghost" className="mb-4 rounded-full border border-white/10 bg-black/20 text-white hover:bg-white/[0.06]">
                    <ArrowLeft className="mr-2 h-4 w-4" />
                    Back to Tournament
                  </Button>
                </Link>
                <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1 text-xs font-medium text-white/65">
                  <BarChart3 className="h-3.5 w-3.5 text-primary" />
                  Dynamic Standings
                </div>
                <h1 className="mt-4 text-3xl font-bold tracking-tight text-white sm:text-4xl">
                  {tournament?.name}
                </h1>
                <p className="mt-3 max-w-2xl text-sm leading-6 text-white/65">
                  Track table movement, sort by the metric that matters most, and compare current standings with a mobile-friendly league view.
                </p>
              </div>

              <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-1">
                <Card className="rounded-3xl border-white/10 bg-black/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">Clubs</p>
                    <p className="mt-2 text-3xl font-bold text-white score-display">{pointsTable.length}</p>
                  </CardContent>
                </Card>
                <Card className="rounded-3xl border-white/10 bg-black/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">Sort Metric</p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {sortOptions.find((option) => option.key === sortKey)?.label}
                    </p>
                  </CardContent>
                </Card>
                <Card className="rounded-3xl border-white/10 bg-black/20">
                  <CardContent className="p-4 text-center">
                    <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">Direction</p>
                    <p className="mt-2 text-lg font-semibold text-white">
                      {sortDirection === 'desc' ? 'High to Low' : 'Low to High'}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </CardContent>
        </Card>

        {pointsTable.length === 0 ? (
          <Card className="rounded-[28px] border-dashed border-white/10 bg-white/[0.04]" data-testid="no-points-card">
            <CardContent className="flex flex-col items-center justify-center py-14">
              <Trophy className="mb-4 h-16 w-16 text-white/20" />
              <h3 className="text-lg font-semibold text-white">No standings yet</h3>
              <p className="mt-2 text-white/45">Complete some matches to bring the league table to life.</p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="rounded-3xl border border-white/10 bg-white/[0.04] p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-white/35">Sort Standings</p>
                  <p className="mt-1 text-sm text-white/55">
                    Tap any metric to reorder the full table with current tournament context.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {sortOptions.map((option) => (
                    <SortChip
                      key={option.key}
                      active={sortKey === option.key}
                      onClick={() => handleSort(option.key)}
                    >
                      <span className="inline-flex items-center gap-2">
                        {option.label}
                        <ArrowUpDown className="h-3.5 w-3.5" />
                      </span>
                    </SortChip>
                  ))}
                </div>
              </div>
            </div>

            <Card className="hidden rounded-[28px] border-white/10 bg-white/[0.04] lg:block" data-testid="points-table-card">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Trophy className="h-5 w-5 text-primary" />
                  League Table
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 bg-white/[0.04] hover:bg-white/[0.04]">
                      <TableHead className="w-14 text-center text-white/45">#</TableHead>
                      <TableHead className="text-white/45">Team</TableHead>
                      <TableHead className="text-center text-white/45">
                        <button onClick={() => handleSort('played')} className="mx-auto inline-flex items-center gap-1">
                          P <ArrowUpDown className="h-3.5 w-3.5" />
                        </button>
                      </TableHead>
                      <TableHead className="text-center text-white/45">
                        <button onClick={() => handleSort('won')} className="mx-auto inline-flex items-center gap-1">
                          W <ArrowUpDown className="h-3.5 w-3.5" />
                        </button>
                      </TableHead>
                      <TableHead className="text-center text-white/45">L</TableHead>
                      <TableHead className="text-center text-white/45">T</TableHead>
                      <TableHead className="text-center text-white/45">
                        <button onClick={() => handleSort('nrr')} className="mx-auto inline-flex items-center gap-1">
                          NRR <ArrowUpDown className="h-3.5 w-3.5" />
                        </button>
                      </TableHead>
                      <TableHead className="text-center text-white/45">
                        <button onClick={() => handleSort('points')} className="mx-auto inline-flex items-center gap-1">
                          Pts <ArrowUpDown className="h-3.5 w-3.5" />
                        </button>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sortedTable.map((team, index) => (
                      <TableRow
                        key={team.team_id}
                        className={cn(
                          'border-white/10 bg-transparent transition-all duration-200 hover:bg-white/[0.04]',
                          index < 2 && 'bg-emerald-500/[0.06]',
                          index === sortedTable.length - 1 && 'bg-red-500/[0.04]'
                        )}
                        data-testid={`points-row-${team.team_id}`}
                      >
                        <TableCell className="text-center font-bold text-white">
                          {index === 0 ? <Trophy className="inline h-4 w-4 text-yellow-400" /> : index + 1}
                        </TableCell>
                        <TableCell className="font-medium text-white">{team.team_name}</TableCell>
                        <TableCell className="text-center text-white/70">{team.played}</TableCell>
                        <TableCell className="text-center font-medium text-emerald-300">{team.won}</TableCell>
                        <TableCell className="text-center font-medium text-red-300">{team.lost}</TableCell>
                        <TableCell className="text-center text-white/70">{team.tied}</TableCell>
                        <TableCell className="text-center">
                          <span className={`inline-flex items-center justify-center gap-1 ${nrrTone(team.nrr)}`}>
                            {team.nrr >= 0 ? <TrendingUp className="h-3.5 w-3.5" /> : <TrendingDown className="h-3.5 w-3.5" />}
                            {team.nrr > 0 ? '+' : ''}
                            {team.nrr.toFixed(3)}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-lg font-bold text-white score-display">{team.points}</span>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <div className="grid gap-4 lg:hidden">
              {sortedTable.map((team, index) => (
                <Card
                  key={team.team_id}
                  className={cn(
                    'rounded-[26px] border-white/10 bg-white/[0.04] transition-all duration-200',
                    index < 2 && 'border-emerald-500/20 bg-emerald-500/[0.06]',
                    index === sortedTable.length - 1 && 'border-red-500/20 bg-red-500/[0.05]'
                  )}
                  data-testid={`points-card-${team.team_id}`}
                >
                  <CardContent className="p-5">
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-semibold uppercase tracking-[0.24em] text-white/35">
                            Position {index + 1}
                          </span>
                          {index === 0 && <Trophy className="h-4 w-4 text-yellow-400" />}
                        </div>
                        <h3 className="mt-2 text-lg font-semibold text-white">{team.team_name}</h3>
                      </div>
                      <Badge variant="secondary" className="rounded-full">
                        {team.points} pts
                      </Badge>
                    </div>

                    <div className="mt-5 grid grid-cols-4 gap-3">
                      <div className="rounded-2xl border border-white/10 bg-black/20 p-3 text-center">
                        <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">P</p>
                        <p className="mt-2 text-lg font-bold text-white">{team.played}</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-black/20 p-3 text-center">
                        <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">W</p>
                        <p className="mt-2 text-lg font-bold text-emerald-300">{team.won}</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-black/20 p-3 text-center">
                        <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">L</p>
                        <p className="mt-2 text-lg font-bold text-red-300">{team.lost}</p>
                      </div>
                      <div className="rounded-2xl border border-white/10 bg-black/20 p-3 text-center">
                        <p className="text-[11px] uppercase tracking-[0.24em] text-white/35">T</p>
                        <p className="mt-2 text-lg font-bold text-white">{team.tied}</p>
                      </div>
                    </div>

                    <div className="mt-4 flex items-center justify-between rounded-2xl border border-white/10 bg-black/20 px-4 py-3">
                      <p className="text-sm text-white/55">Net Run Rate</p>
                      <span className={`inline-flex items-center gap-1 text-sm font-semibold ${nrrTone(team.nrr)}`}>
                        {team.nrr >= 0 ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
                        {team.nrr > 0 ? '+' : ''}
                        {team.nrr.toFixed(3)}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <Card className="rounded-[28px] border-white/10 bg-white/[0.04]">
              <CardHeader className="pb-3">
                <CardTitle className="flex items-center gap-2 text-white">
                  <Sparkles className="h-5 w-5 text-primary" />
                  Table Guide
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 md:grid-cols-3">
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <p className="font-semibold text-white">Dynamic Sorting</p>
                  <p className="mt-2 text-sm leading-6 text-white/55">
                    Tap `Points`, `Run Rate`, `Wins`, or `Played` to instantly reorder the standings.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <p className="font-semibold text-white">Qualification Zones</p>
                  <p className="mt-2 text-sm leading-6 text-white/55">
                    The top teams are lightly highlighted, while the bottom side is toned in subtle red to make table pressure visible.
                  </p>
                </div>
                <div className="rounded-2xl border border-white/10 bg-black/20 p-4">
                  <p className="font-semibold text-white">Future Group Mode</p>
                  <p className="mt-2 text-sm leading-6 text-white/55">
                    This layout is ready to split into group-based mini tables later without changing the core standings component model.
                  </p>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </Layout>
  );
};

export default PointsTable;
